import { useState, useEffect } from 'react'
import { tokens } from '@arvoreeducacao/bonsai-ds'
import { getRootPublicInfo, getUserPublicInfo } from 'api/queries'
import { PAYMENT_PATH, SCHOOL_NOT_FOUND } from 'routes/paths'
import { loginFamilyPayingUserForPayment } from 'api/mutations'
import { hotjarEvent } from 'utils/ui-trackers/hotjar'
import { setCookieOnDomain } from 'utils/cookies'

type NamesObject = {
  id: number
  name: string
}

export const useSchoolAccessRetrieval = ({ match, client, history }) => {
  const [namesFound, setNamesFound] = useState<NamesObject[]>([])
  const [schoolId, setSchoolId] = useState<number>(0)
  const [selectedStudentId, setSelectedStudentId] = useState<number>(0)
  const [noNameFound, setNoNameFound] = useState<boolean>(false)
  const [selectedName, setSelectedName] = useState<string>('')
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
  const [showRequestErrorModal, setShowRequestErrorModal] =
    useState<boolean>(false)
  const [isModalSubscriptionPaidOpen, setIsModalSubscriptionPaidOpen] =
    useState(false)
  const [shouldSearchByEnrollmentNumber, setShouldSearchByEnrollmentNumber] =
    useState<boolean>(false)

  useEffect(() => {
    client
      .query({
        query: getRootPublicInfo,
        variables: {
          removeJWTFromHeader: true,
          code: match.params.code
        }
      })
      .then(res => {
        const { id } = res.data.rootPublicInfo

        setSchoolId(id)
      })
      .catch(err => {
        history.push(SCHOOL_NOT_FOUND)
      })
    const VitalBrazilCode = 'X6359YH'
    setShouldSearchByEnrollmentNumber(match.params.code === VitalBrazilCode)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const eraseField = event => {
    if (
      ['Delete', 'Backspace'].includes(event.key) ||
      window.screen.width <= tokens.sizes.smScreen
    ) {
      setSelectedName('')
      setNoNameFound(false)
    }
  }

  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal)
    hotjarEvent('student_not_found')
  }

  const toggleAlreadyRegisteredModal = () => {
    setShowRequestErrorModal(!showRequestErrorModal)
  }

  const handleStudentUpdate = () => {
    setNamesFound([])
    setSelectedName('')
  }

  const handleOptionClicked = ({ name, id, paid }) => {
    setSelectedName(name)
    setSelectedStudentId(id)
    setNamesFound([])
    if (paid) {
      setSelectedName('')
      setIsModalSubscriptionPaidOpen(true)
    }
  }

  const handleSubmit = () => {
    client
      .mutate({
        mutation: loginFamilyPayingUserForPayment,
        variables: {
          userId: selectedStudentId,
          rootId: schoolId
        }
      })
      .then(res => {
        setCookieOnDomain(
          'access_token',
          res.data.loginUserForPayment.token,
          30
        )

        history.push(
          `${PAYMENT_PATH}?utm_source=${schoolId}&utm_medium=${selectedStudentId}&utm_campaign=acesso_codigo_escola`
        )
        hotjarEvent('payment_using_scholl_code')
      })
      .catch(err => {
        setShowRequestErrorModal(true)
      })
  }

  const searchMatchingNames = (
    baseName: string,
    searchByEnrollmentNumber: boolean
  ) => {
    const baseNameArray = baseName.split(' ')
    const hasOnlyOneWord = baseNameArray.length === 1
    const isSecondWordEmpty = !(!hasOnlyOneWord && baseNameArray[1].length >= 1)

    if ((hasOnlyOneWord || isSecondWordEmpty) && !searchByEnrollmentNumber)
      return setNamesFound([])

    client
      .query({
        query: getUserPublicInfo,
        variables: {
          input: {
            name: baseName,
            rootId: schoolId
          }
        }
      })
      .then(res => {
        const { userPublicInfo } = res.data
        if (!userPublicInfo.length) {
          setNamesFound([])
          setNoNameFound(true)
          return
        }

        setNamesFound(userPublicInfo)
        setNoNameFound(false)
      })
      .catch(_ => setNoNameFound(true))
  }

  const states = {
    namesFound,
    noNameFound,
    selectedName,
    showHelpModal,
    showRequestErrorModal,
    isModalSubscriptionPaidOpen,
    shouldSearchByEnrollmentNumber
  }
  const handlers = {
    searchMatchingNames,
    handleStudentUpdate,
    handleSubmit,
    handleOptionClicked,
    eraseField,
    toggleHelpModal,
    toggleAlreadyRegisteredModal
  }

  return { handlers, states }
}
